import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import CustomDropdown from '../common/CustomDropdown';
import MultiSelectDropdown from '../common/MultiSelectDropdown';
import { getServiceOptions } from '../../config/servicesConfig';
import TAMIL_NADU_DISTRICTS from '../../config/districtsConfig';

const AddUserModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    district: '',
    address: '',
    age: '',
    weight: '',
    gender: '',
    services: [],
    meeting_time: '',
    priority: 'medium'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.number.trim()) {
      newErrors.number = 'Contact number is required';
    } else if (!/^\d{10,}$/.test(formData.number.replace(/\s/g, ''))) {
      newErrors.number = 'Please enter a valid (10 digit) number';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        address: formData.district ? `${formData.district}, ${formData.address}`.trim() : formData.address
      });

      // Reset form
      setFormData({
        name: '',
        number: '',
        district: '',
        address: '',
        age: '',
        weight: '',
        gender: '',
        services: [],
        meeting_time: '',
        priority: 'medium'
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        number: '',
        district: '',
        address: '',
        age: '',
        weight: '',
        gender: '',
        services: [],
        meeting_time: '',
        priority: 'medium'
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  const genderOptions = [
    { value: 'male', label: 'Male', color: 'blue' },
    { value: 'female', label: 'Female', color: 'purple' },
    { value: 'other', label: 'Other', color: 'gray' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'green' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'high', label: 'High', color: 'red' }
  ];

  return (
    <AnimatePresence>
      <div className='fixed inset-0 z-[9999] flex items-end md:items-center justify-center md:p-4'>
        {/* Backdrop */}
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={handleClose}
            className='absolute inset-0 bg-black/60 backdrop-blur-sm'
        />

        {/* Modal Content */}
        <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{
                type: 'spring',
                damping: 30,
                stiffness: 300,
                mass: 0.8,
            }}
            className='relative w-full max-h-[90vh] md:max-h-[85vh] md:max-w-2xl bg-white rounded-t-3xl md:rounded-2xl shadow-2xl border-t md:border border-slate-200 flex flex-col'
            style={{ overscrollBehavior: 'contain' }}
        >
          {/* Header */}
          <div className='p-4 border-b border-slate-100 bg-slate-50'>
              <div className='flex items-center justify-between'>
                  <div className='flex-1 pr-3'>
                      <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                          New Entry
                      </h3>
                      <h2 className='text-lg md:text-xl font-black text-slate-900 mt-1'>
                          Add New User
                      </h2>
                  </div>
                  <button
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className='p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0 disabled:opacity-50'
                  >
                      <XMarkIcon className='h-5 w-5 text-slate-500 hover:text-slate-900' />
                  </button>
              </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-4 md:p-6 space-y-4">
              {/* Name & Number Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 md:px-4 py-2.5 bg-slate-50 border rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium ${
                      errors.name ? 'border-red-500' : 'border-slate-200'
                    }`}
                    placeholder="Enter full name"
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    className={`w-full px-3 md:px-4 py-2.5 bg-slate-50 border rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium ${
                      errors.number ? 'border-red-500' : 'border-slate-200'
                    }`}
                    placeholder="Enter contact number"
                    disabled={isSubmitting}
                  />
                  {errors.number && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.number}</p>
                  )}
                </div>
              </div>

              {/* District & Address Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                      District
                    </label>
                    <CustomDropdown
                      value={formData.district}
                      onChange={(value) => handleDropdownChange('district', value)}
                      options={TAMIL_NADU_DISTRICTS}
                      placeholder="Select district"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                        Address / Area
                    </label>
                    <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="w-full px-3 md:px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                        placeholder="Area or Locality"
                        disabled={isSubmitting}
                    />
                  </div>
              </div>

              {/* Age, Weight, Gender Row */}
              <div className="grid grid-cols-3 gap-3 md:gap-4">
                <div>
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Age
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                    placeholder="Age"
                    min="0"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Weight (kg)
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    step="0.1"
                    className="w-full px-3 md:px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                    placeholder="kg"
                    min="0"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <CustomDropdown
                      value={formData.gender}
                      onChange={(value) => handleDropdownChange('gender', value)}
                      options={genderOptions}
                      placeholder="Select"
                      disabled={isSubmitting}
                    />
                    {errors.gender && (
                      <p className="text-red-500 text-xs mt-1 font-medium">{errors.gender}</p>
                    )}
                </div>
              </div>
              
              {/* Services - Full Width */}
              <div>
                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                  Services
                </label>
                <MultiSelectDropdown
                  value={formData.services}
                  onChange={(value) => handleDropdownChange('services', value)}
                  options={getServiceOptions()}
                  placeholder="Select services..."
                  disabled={isSubmitting}
                  grouped={true}
                />
              </div>

              {/* Time & Priority Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                      Preferred Meeting Time
                    </label>
                    <input
                      type="text"
                      name="meeting_time"
                      value={formData.meeting_time}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm md:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                      placeholder="e.g., 10:00 AM - 11:00 AM"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                      Priority
                    </label>
                    <CustomDropdown
                      value={formData.priority}
                      onChange={(value) => handleDropdownChange('priority', value)}
                      options={priorityOptions}
                      placeholder="Select priority"
                      disabled={isSubmitting}
                    />
                  </div>
              </div>
            </div>

            {/* Footer */}
            <div className='sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 md:px-6 md:py-4 flex items-center justify-end gap-2 md:gap-3 shadow-lg rounded-b-2xl'>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 md:px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 md:px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isSubmitting ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AddUserModal;
